import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { TransformCta } from '../TransformCta';
import { IMAGES } from '../../lib/constants';
type Bonus = {
  number: string;
  title: string;
  description: string;
  bullets?: string[];
  value: string;
  image: string;
};
const BONUSES: Bonus[] = [{
  number: '#1',
  title: 'Calculadora Inteligente de Honorarios Clínicos',
  description: 'Calcula el valor real de cada tratamiento según su complejidad diagnóstica y protege la rentabilidad de tu consulta.',
  bullets: ['Calculadora editable.', 'Fórmula de rentabilidad clínica.', 'Escenarios de tratamiento.', 'Honorarios basados en tiempo y complejidad.'],
  value: '$27 USD',
  image: IMAGES.bono1
}, {
  number: '#2',
  title: 'Protocolo de Presentación del Plan de Tratamiento',
  description: 'Aprende a explicar tu diagnóstico de una forma tan clara que el paciente comprenda el valor del tratamiento antes de pensar en el precio.',
  bullets: ['Cómo presentar fotografías.', 'Cómo justificar el tratamiento.', 'Cómo comunicar casos complejos.', 'Cómo responder objeciones frecuentes.'],
  value: '$29 USD',
  image: IMAGES.bono2
}, {
  number: '#3',
  title: 'Biblioteca de 20 Casos Clínicos Resueltos',
  description: 'Observa cómo un especialista analiza, interpreta y toma decisiones clínicas en casos reales de principio a fin.',
  bullets: ['Fotografías clínicas.', 'Diagnóstico paso a paso.', 'Justificación de cada decisión.', 'Errores frecuentes y aprendizajes de cada caso.'],
  value: '$47 USD',
  image: IMAGES.bono3
}, {
  number: '#4',
  title: 'Biblioteca de los 50 Errores de Diagnóstico Facial',
  description: 'Evita las equivocaciones que más tiempo, dinero y retratamientos generan en la práctica clínica. Aprende a detectarlas antes de que afecten tus resultados.',
  bullets: ['Los 50 errores más frecuentes explicados uno por uno.', 'Cómo detectar cada error a tiempo.', 'Consecuencias clínicas de pasarlo por alto.', 'La forma correcta de corregirlo.'],
  value: '$37 USD',
  image: IMAGES.bono4
}, {
  number: '#5',
  title: 'Checklist Maestro de Diagnóstico en 15 Minutos',
  description: 'La herramienta que utilizarás antes de aprobar cualquier plan de tratamiento. Una hoja clínica diseñada para validar que no has pasado por alto ningún aspecto importante del diagnóstico.',
  bullets: ['Protocolo completo en 4 pasos: observa, analiza, integra y decide.', 'Checklist facial, dental y cefalométrico en una sola hoja.', 'Hoja de resumen del caso y guía rápida de verificación.', 'Imprimible y reutilizable con cada paciente.'],
  value: '$27 USD',
  image: IMAGES.bono5
}];
export function Bonuses() {
  return <section className="w-full bg-white px-4 py-14 sm:py-20" data-id="element-73" data-mp="1dekmleb63a9d">
      <div className="mx-auto max-w-3xl" data-id="element-74" data-mp="139m14xp4y2kz">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-gold-600" data-id="element-75" data-mp="136gfxtjmnkuj">
          🎁 Bonos exclusivos
        </p>
        <h2 className="mt-3 text-center font-display text-[26px] font-bold leading-[1.15] tracking-tight text-navy-900 sm:text-4xl" data-id="element-76" data-mp="1d6gyvedl3yy7">
          Además, recibirás estos 5 Bonos Exclusivos totalmente <span className="text-gold-600" data-id="element-77" data-mp="1lzk9lewuhxp5">GRATIS</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-[15px] leading-relaxed text-navy-600 sm:text-[17px]" data-id="element-78" data-mp="1m157neidbyrr">
          Porque un buen diagnóstico no depende solo del conocimiento. También depende de contar con
          las herramientas adecuadas.
        </p>

        <ul className="mt-9 space-y-5" data-id="element-79" data-mp="20v0ao3vuetv1">
          {BONUSES.map((bonus, index) => <motion.li key={bonus.number} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.25
        }} transition={{
          duration: 0.4,
          delay: Math.min(index * 0.04, 0.16)
        }} className="overflow-hidden rounded-3xl border border-navy-100 bg-cream shadow-card" data-id="element-80" data-mp="1k8tsq4xh16w" data-mpi={index}>
              <img src={bonus.image} alt={`Bono ${bonus.number}: ${bonus.title}`} className="w-full bg-white object-cover" loading="lazy" data-id="element-81" data-mp="25z12q3upa4wa" />

              <div className="p-5" data-id="element-82" data-mp="1dby3tam43s5i">
                <div className="flex flex-wrap items-center gap-2" data-id="element-83" data-mp="199tfmrefnnq9">
                  <span className="rounded-full bg-navy-800 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-gold-300" data-id="element-84" data-mp="19bo1c7xj3jve">
                    Bono {bonus.number}
                  </span>
                  <span className="rounded-full bg-gold-100 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-gold-700" data-id="element-85" data-mp="1i96q9neamick">
                    Valor: {bonus.value}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-[18px] font-bold leading-tight text-navy-900 sm:text-xl" data-id="element-86" data-mp="3197bybhq9ag8">
                  {bonus.title}
                </h3>
                <p className="mt-2.5 text-[14.5px] leading-relaxed text-navy-600 sm:text-base" data-id="element-87" data-mp="361h1hqexk8hz">
                  {bonus.description}
                </p>
                {bonus.bullets ? <ul className="mt-3.5 space-y-2 border-t border-dashed border-navy-200 pt-3.5" data-id="element-88" data-mp="2x83x9sbf1kgl">
                    {bonus.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2" data-id="element-89" data-mp="3oyi454l0hwir">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" data-id="element-90" data-mp="3owfiewqw6pju" />
                        <span className="text-[13.5px] leading-snug text-navy-700 sm:text-[14.5px]" data-id="element-91" data-mp="3fw8xipsv6nan">
                          {bullet}
                        </span>
                      </li>)}
                  </ul> : null}
              </div>
            </motion.li>)}
        </ul>

        <p className="mt-8 text-center font-display text-[18px] font-semibold leading-snug text-navy-900 sm:text-xl" data-id="element-92" data-mp="1gf73h2i3onom">
          Todo este sistema podría venderse fácilmente por separado.
          <span className="mt-1 block text-gold-600" data-id="element-93" data-mp="1bm5m8nuf8c1v">Pero hoy lo recibirás completo.</span>
        </p>

        <TransformCta headline="Súmalos hoy a tu consulta sin pagar un peso extra por los bonos." label="Quiero el sistema + los 5 bonos" location="bonos" data-id="element-94" data-mp="16nkuzdx5d2pk" />
      </div>
    </section>;
}